import { SortingOrder } from "src/graphql/anti-fraud/__generated__/graphql.ts";
import { URLParamConverter, URLParamType, useURLParams } from "src/hooks/useURLParams.ts";

type TableParamsType = {
  offset: number;
  searchTerm: string;
  index?: number;
  sortBy?: string;
  sortOrder?: SortingOrder;
};

type MergeWithTableParamsType<T> = T & TableParamsType;

const DEFAULT_PARAMS: TableParamsType = {
  offset: 0,
  searchTerm: "",
  index: 0,
  sortBy: undefined,
  sortOrder: undefined,
};

const DEFAULT_CONVERTERS = {
  offset: (value: string | null) => (value ? parseInt(value, 10) : 0),
  index: (value: string | null) => (value ? parseInt(value, 10) : undefined),
  searchTerm: (value: string | null) => value,
  sortBy: (value: string | null) => value,
  sortOrder: (value: string | null) => value,
};

export const useTableURLParams = <T extends Record<string, URLParamType>>(
  params: T,
  converters: URLParamConverter<T>
) => {
  const { urlParams, updateSearchParams } = useURLParams<MergeWithTableParamsType<T>>(
    { ...DEFAULT_PARAMS, ...params },
    { ...DEFAULT_CONVERTERS, ...converters } as URLParamConverter<MergeWithTableParamsType<T>>
  );

  const handleOnSortChange = ({ sortBy, sortOrder }) => {
    updateSearchParams({
      sortBy,
      sortOrder,
      index: null,
      offset: null,
    } as unknown as MergeWithTableParamsType<T>);
    handleOnOffsetChange();
  };

  const handleOnOffsetChange = (offset?: number, index?: number) => {
    updateSearchParams({ offset, index } as MergeWithTableParamsType<T>);
  };

  const handleOnSearchTermChange = (searchTerm: string) => {
    // TODO fix the type issue

    updateSearchParams({
      searchTerm,
      offset: null,
      index: null,
    } as unknown as MergeWithTableParamsType<T>);
  };

  const handleOnFiltersChange = (filters: Record<string, URLParamType>) => {
    updateSearchParams({
      ...filters,
      offset: null,
      index: null,
    } as unknown as MergeWithTableParamsType<T>);
  };

  return {
    ...urlParams,
    handleOnOffsetChange,
    handleOnSearchTermChange,
    handleOnFiltersChange,
    handleOnSortChange,
  };
};
